/**
 * SPR quiz. Two-step: effective stack = min(hero, villain), SPR = eff/pot.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { Stat } from "@/features/quiz/components/Stat";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import { type SprQuestion, generateQuestion, sprBand } from "@/features/quiz/lib/sprQuiz";

export function QuizSpr() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<SprQuestion, number>({
      quizType: "spr",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.answer,
      getQuestionPayload: (q) => ({
        pot: q.pot,
        hero_stack: q.heroStack,
        villain_stack: q.villainStack,
      }),
    });

  const whoIsShort = question.heroStack === question.villainStack
    ? "both stacks are equal"
    : question.effectiveStack === question.heroStack
    ? "hero is the short stack"
    : "villain is the short stack";

  return (
    <QuizLayout
      Icon={Brain}
      title="SPR"
      subtitle="Stack-to-Pot Ratio (don't forget the effective stack)"
      description="Tests stack-to-pot ratio using the effective (smaller) stack. SPR predicts how much pressure is on stack-off decisions: low SPR is commit-or-fold with strong hands, high SPR demands more careful play. Choosing a postflop plan without it is guessing."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="grid grid-cols-3 gap-6">
          <Stat label="HERO" value={`${question.heroStack}bb`} size="sm" />
          <Stat label="VILLAIN" value={`${question.villainStack}bb`} size="sm" />
          <Stat label="POT" value={`${question.pot}bb`} accent size="sm" />
        </div>
      </div>

      <p className="text-center text-text text-base">What's the SPR?</p>

      <div className="grid grid-cols-2 gap-3">
        {question.options.map((spr) => (
          <QuizOption
            key={spr}
            value={spr}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{spr}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>SPR {correctAnswer}</strong>
            </>
          }
          rationale={
            <>
              Effective stack = min({question.heroStack}, {question.villainStack}) = <strong>{question.effectiveStack}bb</strong> ({whoIsShort}). SPR = {question.effectiveStack} / {question.pot} = <strong>{correctAnswer}</strong>. {sprBand(correctAnswer)}.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}

